/**
 * Seeded analyst consensus (F15, plan §3.3).
 *
 * Three positional draws from the `ticker + 'a'` stream, IN THIS ORDER — nA,
 * buy ratio, hold ratio — because a mulberry32 stream is positional: reordering
 * the draws regenerates every consensus row in the app with no test failure and
 * no visual anomaly (plan §0.3 R1). Target reuses `buy/nA` rather than a fourth
 * draw, so the price target moves with the recommendation rather than
 * independently of it.
 *
 * Live v1 has NO licensed consensus provider, so `HttpRepository` returns
 * `consensus: null` and the UI renders "Consensus unavailable in live mode"
 * (plan §6.5a). When one is licensed, the counts arrive from the provider and
 * only `verdictFor` and the target formula are reused — which is why the counts
 * are an optional override rather than baked into the draw.
 */
import {
  VERDICT_THRESHOLDS,
  type AnalystConsensus,
  type Ticker,
  type Verdict,
} from '@meridian/contracts';

import {
  ANALYST_BASE,
  ANALYST_SPAN,
  BUY_RATIO_LO,
  BUY_RATIO_SPAN,
  HOLD_RATIO_LO,
  HOLD_RATIO_SPAN,
  TARGET_BIAS,
  TARGET_PAD,
  TARGET_SCALE,
} from '../constants/consensus';
import { fnv1a } from '../rand/fnv1a';
import { createRng } from '../rand/mulberry32';
import { seedConsensus } from '../rand/seed';

export interface ConsensusSubject {
  readonly ticker: Ticker;
  readonly price: number;
}

/** Provider-supplied counts, for the live path once §6.5a is resolved. */
export interface ConsensusCounts {
  readonly nA: number;
  readonly buy: number;
  readonly hold: number;
}

/**
 * Strict `>` at BOTH boundaries (plan §4.4): a ratio of exactly 0.55 is HOLD,
 * not BUY, and exactly 0.35 is REDUCE, not HOLD. `>=` here would move every
 * boundary row up one verdict.
 *
 * `nA` must be positive — a zero/negative analyst count is not a valid consensus
 * (wire schema requires nA > 0); fail closed rather than emit Infinity/NaN.
 */
export function verdictFor(buy: number, nA: number): Verdict {
  if (!(nA > 0)) {
    throw new Error(`verdictFor: nA must be positive, got ${String(nA)}`);
  }
  const ratio = buy / nA;
  if (ratio > VERDICT_THRESHOLDS.buy) return 'BUY';
  if (ratio > VERDICT_THRESHOLDS.hold) return 'HOLD';
  return 'REDUCE';
}

function draw(subject: ConsensusSubject): ConsensusCounts {
  const R = createRng(fnv1a(seedConsensus(subject.ticker)));
  const nA = ANALYST_BASE + Math.floor(R() * ANALYST_SPAN);
  const buy = Math.round(nA * (BUY_RATIO_LO + R() * BUY_RATIO_SPAN));
  const hold = Math.round((nA - buy) * (HOLD_RATIO_LO + R() * HOLD_RATIO_SPAN));
  return { nA, buy, hold };
}

export function computeConsensus(
  subject: ConsensusSubject,
  counts?: ConsensusCounts,
): AnalystConsensus {
  const { nA, buy, hold } = counts ?? draw(subject);
  if (!(nA > 0)) {
    throw new Error(`computeConsensus: nA must be positive, got ${String(nA)}`);
  }
  return {
    ticker: subject.ticker,
    nA,
    buy,
    hold,
    /* Clamped at 0 so a provider's inconsistent triple cannot emit a negative
       count that the wire schema would then reject three layers away. */
    sell: Math.max(0, nA - buy - hold),
    target: subject.price * (1 + (buy / nA - TARGET_BIAS) * TARGET_SCALE + TARGET_PAD),
    verdict: verdictFor(buy, nA),
  };
}
